import { resolveEntityPrefab, type LibraryCatalog } from '../assets/core/catalog';
import { LIB_SCHEME, parseLibUri } from '../assets/core/uri';
import type { AssetHandle, EntityPlacement } from '../assets/handle';
import type { AssetService } from '../assets/service';
import type { EntityHost } from '../entities/host';
import type { EntityInstance } from '../entities/types';
import type { WorldPrefab } from '../world/prefabs';

/**
 * ── ⭐ P1.5·C · SUMMONING A LIBRARY ENTITY — THE SEAM, AND WHY IT IS IN THE APP LAYER ─────────────
 *
 * Plan §13.6: *"the service resolves the definition; the SPAWN itself goes through `EntityHost` in
 * the app layer, because `src/assets/**` may not import `src/entities/**`."* That boundary
 * (ADR-0001, `tests/entityBoundary.test.ts`) is not a technicality to route around — it is what
 * keeps the eager core free of every entity class — so this module is where the two halves meet,
 * and it is the ONLY place they do. It imports both sides; neither side imports the other.
 *
 * ⚠️ AND IT IS THE SAME SEAM SHAPE `registryClasses` ALREADY USES. The service is handed a THUNK
 * that names classes (`() => new Set(entityTypeNames())`) rather than the registry; the host is
 * handed a THUNK that resolves a library id (`HostLibrary.prefab`) rather than the service. Two
 * one-way callbacks, wired by mount, and each side stays ignorant of the other's module graph.
 *
 * ── THE THREE DOORS, AND THE ONE ANSWER BEHIND THEM ─────────────────────────────────────────────
 *
 *  · `prefab(id)`    the HOST's arm: a `lib:<id>` PLACEMENT becomes a prefab-table row, so
 *                    ADR-0002's precedence stays the one merge rule in the codebase.
 *  · `retain(world)` the PLACEMENT's LIFETIME: one handle per distinct `lib:` id a world places,
 *                    held while the world is loaded. The prefab's declared `data` assets are its
 *                    dependency closure, so this is what makes "declared at the entity level, so
 *                    dependencies are handled automatically" true for a placed entity.
 *  · `summon(id)`    the RUNTIME door (`assets.spawn` in the plan's words): acquire, show a
 *                    placeholder, spawn on ready, and hand back a release that disposes both.
 *
 * All three read `resolveEntityPrefab` — the ONE catalog→prefab fold — so a placement, a summon
 * and the service's own `EntitySource` can never disagree about what an id means.
 *
 * ── LANE: `gameplay`, FOR BOTH DOORS, AND THAT IS DELIBERATE ────────────────────────────────────
 *
 * §13.6 requires the summon to run in the gameplay lane *"so it never gates boot"*. A PLACEMENT is
 * world content and could argue for the boot lane — and it does not get it, for the reason the
 * class's own placeholder policy gives: a sim whose skin has not arrived draws its stock skin,
 * which is a picture rather than an error, so holding the loading plate for it would be spending a
 * player's boot on a difference they cannot see. The boot ladder is untouched by this stage.
 *
 * NO THREE, NO DOM: the placeholder is an injected factory, so this module — and the proof that
 * drives it — runs headlessly. Mount supplies a real marker; a test supplies a counter or nothing.
 */

/** What a summon can put on screen while the prefab's data is in flight. App-layer, injected. */
export interface SpawnPlaceholder {
  dispose(): void;
}

export interface LibrarySpawnDeps {
  /** The ONE service. Only `catalog()` and `acquire()` are used — no lifetime lives here but the handles. */
  assets: Pick<AssetService, 'catalog' | 'acquire'>;
  /** The ONE host. `spawn` validates, lazy-loads and constructs; `remove` disposes and forgets. */
  host: Pick<EntityHost, 'spawn' | 'remove'>;
  /**
   * The placeholder policy (§4.2's table, `entity` row). Called synchronously at summon time with
   * the placement that WILL be spawned; disposed the moment the real instance lands, or on release.
   * Absent ⇒ no placeholder, which is the headless case and the honest degrade.
   */
  placeholder?: (def: EntityPlacement, id: string) => SpawnPlaceholder | null;
  /** Default `console.warn`. Every message goes through here ONCE per key (rule 6). */
  warn?: (message: string) => void;
}

export interface SummonOptions {
  /**
   * Fields written OVER the prefab's params — the placement half of ADR-0002's precedence, applied
   * by the same rule the host applies to a world placement. `type` may not be overridden: the
   * asset's class is what `requires.classes` gated, and letting a caller swap it would make the
   * gate a statement about a different class.
   */
  overrides?: Record<string, unknown>;
}

/** What a summon hands back. Everything is available SYNCHRONOUSLY except the instance itself. */
export interface SummonedEntity {
  id: string;
  /** The library handle. Held until `release()`; its deps are the prefab's declared data assets. */
  handle: AssetHandle<'entity'>;
  /**
   * Resolves to the live instance, or `null` when the asset was unavailable or the host refused
   * the def. NEVER rejects — a summon that could not happen is a state, not an exception (rule 6).
   */
  ready: Promise<EntityInstance | null>;
  /** Idempotent: disposes the placeholder, removes the instance, releases the handle. */
  release(): void;
}

export interface LibraryEntities {
  /** The host's `lib:` arm — see {@link HostLibrary}. */
  prefab(id: string): WorldPrefab | null;
  /**
   * Hold one handle per distinct `lib:` id in a placement list, releasing whatever the previous
   * call held. Returns the ids retained. Idempotent per id: a re-retain of the same set moves no
   * refcount and issues no fetch, because the ledger's grace period has not started.
   */
  retain(entities: readonly unknown[]): string[];
  summon(id: string, opts?: SummonOptions): SummonedEntity;
  /** Release every retained handle and every live summon — the world teardown / unmount path. */
  disposeAll(): void;
}

/** `lib:<id>` ids named by a placement list, in first-seen order and deduplicated. */
export function libraryPlacementIds(entities: readonly unknown[]): string[] {
  const out: string[] = [];
  for (const e of entities) {
    if (typeof e !== 'object' || e === null) continue;
    const type = (e as { type?: unknown }).type;
    if (typeof type !== 'string' || !type.startsWith(LIB_SCHEME)) continue;
    const id = parseLibUri(type);
    if (id !== null && !out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * The catalog's entry for a library entity, as a PREFAB TABLE ROW. `null` — never a throw — when
 * the id is unknown, is not an `entity`, or carries no block; the reason is the fold's own
 * sentence, warned once by the caller below.
 */
export function libraryPrefabOf(catalog: LibraryCatalog, id: string): { prefab: WorldPrefab | null; warnings: readonly string[] } {
  const resolved = resolveEntityPrefab(catalog, id);
  if (!resolved.found) return { prefab: null, warnings: resolved.warnings };
  return { prefab: { extends: resolved.class, params: { ...resolved.params } }, warnings: [] };
}

export function createLibraryEntities(deps: LibrarySpawnDeps): LibraryEntities {
  const emit = deps.warn ?? ((m: string) => console.warn(m));
  const warned = new Set<string>();
  const warnOnce = (key: string, message: string): void => {
    if (warned.has(key)) return;
    warned.add(key);
    emit(message);
  };

  /** id → the handle held for a PLACED prefab. One per id, however many placements use it. */
  const retained = new Map<string, AssetHandle<'entity'>>();
  const live = new Set<SummonedEntity>();

  const prefab = (id: string): WorldPrefab | null => {
    const { prefab: row, warnings } = libraryPrefabOf(deps.assets.catalog(), id);
    for (const w of warnings) warnOnce(`prefab:${id}:${w}`, `[assets] ${w}`);
    return row;
  };

  const retain = (entities: readonly unknown[]): string[] => {
    const wanted = libraryPlacementIds(entities);
    // Acquire the newcomers BEFORE releasing the leavers: an id present in both sets must never
    // drop to a refcount of zero in between, or a world reload would dispose and re-fetch every
    // shared data asset it is about to ask for again.
    for (const id of wanted) {
      if (retained.has(id)) continue;
      retained.set(id, deps.assets.acquire(id, { lane: 'gameplay', kind: 'entity' }));
    }
    for (const [id, handle] of [...retained]) {
      if (wanted.includes(id)) continue;
      handle.release();
      retained.delete(id);
    }
    return wanted;
  };

  const summon = (id: string, opts: SummonOptions = {}): SummonedEntity => {
    const handle = deps.assets.acquire(id, { lane: 'gameplay', kind: 'entity' });
    let released = false;
    let placeholder: SpawnPlaceholder | null = null;
    let instance: EntityInstance | null = null;

    /*
     * The placement is built from the CATALOG, synchronously, so the placeholder can be posed
     * where the real thing will be. `handle.instance()` is not available yet (it is null outside
     * `ready`, by contract), and this is not a second answer: `libraryPrefabOf` and `instance()`
     * are both `resolveEntityPrefab` reading the same entry.
     */
    const { prefab: row } = libraryPrefabOf(deps.assets.catalog(), id);
    const provisional: EntityPlacement | null =
      row === null ? null : { ...row.params, ...(opts.overrides ?? {}), type: row.extends };
    if (provisional !== null) placeholder = deps.placeholder?.(provisional, id) ?? null;

    const ready = handle.ready.then(async (loaded): Promise<EntityInstance | null> => {
      if (released) return null;
      if (!loaded.ok) {
        warnOnce(
          `summon:${id}`,
          `[assets] summon of '${id}' failed (${loaded.failure?.code ?? 'unknown'}: ${loaded.failure?.message ?? ''}) — nothing spawned`,
        );
        placeholder?.dispose();
        placeholder = null;
        return null;
      }
      const base = handle.instance();
      if (base === null) return null;
      // `type` LAST: the asset's class is what the capability gate refused or allowed, and an
      // override that could rename it would make that gate a statement about a different class.
      const def: EntityPlacement = { ...base, ...(opts.overrides ?? {}), type: base.type };
      const inst = await deps.host.spawn(def);
      if (released) {
        // Released while the chunk was importing: the instance exists and nobody wants it.
        if (inst !== null) deps.host.remove(inst);
        return null;
      }
      instance = inst;
      placeholder?.dispose();
      placeholder = null;
      if (inst === null) {
        warnOnce(`summon-host:${id}`, `[assets] the entity host refused the summoned def for '${id}' — nothing spawned`);
      }
      return inst;
    });

    const summoned: SummonedEntity = {
      id,
      handle,
      ready,
      release() {
        if (released) return;
        released = true;
        live.delete(summoned);
        placeholder?.dispose();
        placeholder = null;
        if (instance !== null) deps.host.remove(instance);
        instance = null;
        handle.release();
      },
    };
    live.add(summoned);
    return summoned;
  };

  const disposeAll = (): void => {
    for (const s of [...live]) s.release();
    for (const handle of retained.values()) handle.release();
    retained.clear();
  };

  return { prefab, retain, summon, disposeAll };
}
